/**
 * useIsOrgApprover Hook
 *
 * V168: 현재 사용자가 조직관리에서 승인자로 지정되어 있는지 확인
 * V169: 승인자 결정 SSoT 로직 적용 (확장)
 * V169.2: 모듈 레벨 TTL 캐시 추가 (성능 최적화)
 *
 * **목적:**
 * - role과 무관하게 조직관리에서 승인자로 지정된 사용자는 승인관리 페이지에 접근 가능
 * - Sidebar 메뉴 노출 및 라우트 접근 권한 체크에 사용
 *
 * **V169: 승인자 결정 SSoT 로직:**
 * 1. orgUnits.approverEmployeeId가 있으면 해당 직원이 승인자
 * 2. 없으면 해당 조직 내 role_manager 역할자 중 사번(employeeNumber) 오름차순 1명
 * 3. 없으면 해당 조직 내 role_exec 역할자 중 사번 오름차순 1명
 * 4. 모두 없으면 승인자 없음
 *
 * **V169.2: 성능 최적화**
 * - Sidebar/ProtectedRoute 등 여러 컴포넌트에서 동시 호출 시 중복 쿼리 방지
 * - 모듈 레벨 캐시로 30초 TTL 적용
 * - 동일 employeeId에 대해 진행 중인 요청 공유 (Promise dedup)
 *
 * **사용 예시:**
 * ```tsx
 * const { isOrgApprover, approverOrgUnits, isLoading } = useIsOrgApprover();
 *
 * if (isOrgApprover) {
 *   // 승인관리 메뉴 표시
 * }
 * ```
 */

import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, or } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useCurrentUser } from './useCurrentUser';
import { OrgUnit } from '../../types/orgUnit';
import { getOrgUnitsWhereUserIsApprover } from '../utils/resolveApprover';

// ===== V169.2: 모듈 레벨 캐시 =====
const CACHE_TTL_MS = 30_000; // 30초

interface CacheEntry {
  orgUnits: OrgUnit[];
  timestamp: number;
}

// employeeId → CacheEntry
const approverCache = new Map<string, CacheEntry>();

// 진행 중인 요청 공유 (Promise dedup)
const pendingRequests = new Map<string, Promise<OrgUnit[]>>();

/**
 * V169.2: 캐시된 결과 조회 또는 새 요청 실행
 */
async function getCachedOrFetch(employeeId: string): Promise<OrgUnit[]> {
  // 1. 유효한 캐시 확인
  const cached = approverCache.get(employeeId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.orgUnits;
  }

  // 2. 진행 중인 요청이 있으면 공유
  const pending = pendingRequests.get(employeeId);
  if (pending) {
    return pending;
  }

  // 3. 새 요청 시작
  const promise = getOrgUnitsWhereUserIsApprover(db!, employeeId)
    .then((orgUnits) => {
      // 캐시 저장
      approverCache.set(employeeId, {
        orgUnits,
        timestamp: Date.now(),
      });
      return orgUnits;
    })
    .finally(() => {
      // 완료 후 pending에서 제거
      pendingRequests.delete(employeeId);
    });

  pendingRequests.set(employeeId, promise);
  return promise;
}

export interface UseIsOrgApproverResult {
  /**
   * 현재 사용자가 어떤 조직의 승인자인지 여부
   */
  isOrgApprover: boolean;

  /**
   * 현재 사용자가 승인자로 지정된 조직 목록
   */
  approverOrgUnits: OrgUnit[];

  /**
   * 로딩 상태
   */
  isLoading: boolean;

  /**
   * 에러 상태
   */
  error: Error | null;
}

/**
 * 현재 사용자가 조직관리에서 승인자로 지정되어 있는지 확인하는 Hook
 *
 * **V168: 승인관리 접근 권한 확장**
 * - 기존 RBAC 권한(work.approve) 외에 조직관리 승인자도 승인관리 페이지 접근 가능
 *
 * **V169: 승인자 결정 SSoT 로직 적용**
 * - approverEmployeeId 뿐 아니라 role_manager/role_exec fallback도 고려
 */
export function useIsOrgApprover(): UseIsOrgApproverResult {
  const { user, isLoading: userLoading } = useCurrentUser();
  const [approverOrgUnits, setApproverOrgUnits] = useState<OrgUnit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 현재 사용자의 employeeId (users 컬렉션의 문서 ID)
  const employeeId = user?.profile?.id;

  useEffect(() => {
    if (userLoading) {
      return;
    }

    if (!employeeId || !db) {
      setApproverOrgUnits([]);
      setIsLoading(false);
      return;
    }

    async function fetchApproverOrgUnits() {
      try {
        setIsLoading(true);
        setError(null);

        // V169.2: TTL 캐시 적용 (30초)
        const orgUnits = await getCachedOrFetch(employeeId!);

        setApproverOrgUnits(orgUnits);
      } catch (err) {
        console.error('[V169.2] 승인자 조직 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchApproverOrgUnits();
  }, [employeeId, userLoading]);

  const isOrgApprover = useMemo(() => {
    return approverOrgUnits.length > 0;
  }, [approverOrgUnits]);

  return {
    isOrgApprover,
    approverOrgUnits,
    isLoading: userLoading || isLoading,
    error,
  };
}
